"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { Button } from "@/components/ui/button";
import { Label, Input } from "@/components/ui/form-elements";
import { useGraphStore } from "@/store/graph-store";
import { saveViewAction, loadViewsAction } from "@/actions/view-actions";
import { Save, FolderOpen, Shield, Globe } from "lucide-react";

export function SaveViewDialog() {
  const { allNodes, visibleCategories, loadSavedView } = useGraphStore();
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [savedViewsList, setSavedViewsList] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchViews = async () => {
    const list = await loadViewsAction();
    if (Array.isArray(list)) {
      setSavedViewsList(list);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchViews();
    }
  }, [isOpen]);

  const getViewportFromDOM = () => {
    const viewportEl = document.querySelector(".react-flow__viewport");
    if (!viewportEl) return { x: 0, y: 0, zoom: 1 };
    const style = window.getComputedStyle(viewportEl);
    const transform = style.transform || style.webkitTransform;
    if (!transform || transform === "none") {
      return { x: 0, y: 0, zoom: 1 };
    }
    const match = transform.match(
      /^matrix\(([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+)\)$/,
    );
    if (match) {
      return {
        zoom: parseFloat(match[1]),
        x: parseFloat(match[5]),
        y: parseFloat(match[6]),
      };
    }
    return { x: 0, y: 0, zoom: 1 };
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);

    const viewport = getViewportFromDOM();
    const data = {
      viewport,
      visibleCategories,
      nodes: allNodes.map((n) => ({
        id: n.id,
        position: n.position,
      })),
    };

    const res = await saveViewAction({
      name,
      data,
      isPublic,
    });

    setIsSaving(false);
    if ("error" in res) {
      alert(res.error);
    } else {
      setName("");
      fetchViews();
    }
  };

  const handleLoadView = (view: any) => {
    const data = view.data as any;
    if (!data) return;

    if (Array.isArray(data.nodes) && Array.isArray(data.visibleCategories)) {
      loadSavedView(data.nodes, data.visibleCategories);
    }

    setTimeout(() => {
      const fitBtn = document.querySelector(
        ".react-flow__controls-fitview",
      ) as HTMLButtonElement | null;
      if (fitBtn) {
        fitBtn.click();
      }
    }, 100);

    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="h-8 text-xs font-semibold flex items-center gap-1.5 bg-card border-border hover:bg-card-muted"
        >
          <FolderOpen className="h-3.5 w-3.5" />
          Viste Salvate
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Gestione Viste Grafo</DialogTitle>
          <DialogDescription>
            Salva il viewport corrente, le posizioni dei nodi e i filtri di
            categoria attivi, o carica una vista esistente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3 rounded-lg border border-border p-3 bg-card-muted/50">
            <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Nuova Vista
            </h4>
            <div className="space-y-2">
              <Label htmlFor="view-name">Nome della vista</Label>
              <Input
                id="view-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="es. Visualizzazione Strategica Q3"
                className="h-8 text-xs"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2 select-none">
                <input
                  type="checkbox"
                  id="view-public"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                />
                <Label
                  htmlFor="view-public"
                  className="cursor-pointer font-medium text-text-secondary flex items-center gap-1"
                >
                  {isPublic ? (
                    <Globe className="h-3.5 w-3.5 text-success" />
                  ) : (
                    <Shield className="h-3.5 w-3.5 text-warning" />
                  )}
                  Vista Pubblica (Tutti)
                </Label>
              </div>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!name.trim() || isSaving}
                className="h-7 text-[11px] font-semibold bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-1 border border-transparent shadow-sm"
              >
                <Save className="h-3 w-3" />
                {isSaving ? "Salvataggio..." : "Salva Vista"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Carica Vista Esistente
            </h4>
            {savedViewsList.length === 0 ? (
              <div className="text-xs text-text-muted text-center py-6 border border-dashed border-border rounded-lg">
                Nessuna vista salvata per questo tenant.
              </div>
            ) : (
              <div className="max-h-[180px] overflow-y-auto border border-border rounded-lg divide-y divide-border bg-card">
                {savedViewsList.map((view) => (
                  <div
                    key={view.id}
                    onClick={() => handleLoadView(view)}
                    className="flex items-center justify-between p-2.5 hover:bg-card-muted cursor-pointer transition-colors group"
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <span className="text-xs font-semibold text-text-primary truncate">
                        {view.name}
                      </span>
                      <span className="text-[10px] text-text-muted">
                        Aggiornato il{" "}
                        {new Date(view.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {view.isPublic ? (
                        <span title="Pubblica">
                          <Globe className="h-3 w-3 text-success/75" />
                        </span>
                      ) : (
                        <span title="Privata">
                          <Shield className="h-3 w-3 text-warning/75" />
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity font-semibold"
                      >
                        Apri
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
